import { useState, useEffect } from "react";
import { SEOHead } from "@/components/seo/SEOHead";
import { PAGE_SEO, getCanonicalUrl, getBreadcrumbSchema } from "@/config/seo";
import { Layout } from "@/components/layout/Layout";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Gift, Copy, Share2, Users, Check, Trophy, ArrowRight, Clock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { User } from "@supabase/supabase-js";
import { useReferrals, useLeaderboard } from "@/hooks/useReferrals";
import { MARKETING_CONFIG } from "@/config/marketing";

const Referral = () => {
  const [user, setUser] = useState<User | null>(null);
  const [copied, setCopied] = useState(false);
  const bonusAmt = `$${(MARKETING_CONFIG.referralBonusReferrer / 100).toFixed(0)}`;

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const { 
    referrals, 
    stats, 
    loading, 
    referralCode, 
    referralLink 
  } = useReferrals(user);
  
  const { leaderboard, loading: leaderboardLoading } = useLeaderboard();

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast({ title: "Copied!", description: "Referral link copied to clipboard." });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({ title: "Error", description: "Failed to copy link.", variant: "destructive" });
    }
  };

  const shareViaWhatsApp = () => {
    const text = encodeURIComponent(`Join elefa and we both get ${bonusAmt} credit! Use my referral link: ${referralLink}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent(`Call home with elefa: ${bonusAmt} bonus credit when you join`);
    const body = encodeURIComponent(
      `Hi,\n\nI call my family back home with elefa for clear voice, low rates, and they answer on any phone. No app to install on their end, no Wi-Fi needed for them.\n\nSign up with my link and we both get ${bonusAmt} bonus credit after your first top-up:\n${referralLink}\n\nSee you on elefa.`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };


  const formatDisplayName = (referral: typeof referrals[0]) => {
    // SECURITY: referred_email / referred_name are blocked at the DB layer
    // (column-level REVOKE on public.referrals). Show a status-based label only.
    if (referral.status === 'completed') return 'Joined';
    if (referral.status === 'pending')   return 'Invited';
    return 'Invited';
  };

  return (
    <Layout>
      <SEOHead
        title={PAGE_SEO.referral.title}
        description={PAGE_SEO.referral.description}
        keywords={PAGE_SEO.referral.keywords}
        canonicalUrl={getCanonicalUrl('/referral')}
        structuredData={getBreadcrumbSchema([
          { name: 'Home', url: 'https://elefa.tel/' },
          { name: 'Refer & Earn', url: 'https://elefa.tel/referral' },
        ])}
      />
      {/* Hero */}
      <section className="bg-gradient-to-br from-elefa-cream via-background to-background py-16">
        <div className="container text-center">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-accent/20 px-4 py-2 text-sm font-medium text-accent-foreground">
            <Gift className="h-4 w-4" />
            Referral Program
          </div>
          <h1 className="mb-4 text-4xl font-bold text-foreground sm:text-5xl">
            Give {bonusAmt}, Get <span className="text-gradient">{bonusAmt}</span>
          </h1>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
            Share elefa with friends and family. When they sign up and add credit, you both get {bonusAmt} credit.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="border-b border-border py-12">
        <div className="container">
          <h2 className="mb-8 text-center text-2xl font-bold text-foreground">
            How It Works
          </h2>
          <div className="mx-auto grid max-w-3xl gap-8 md:grid-cols-3">
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-hero text-2xl font-bold text-primary-foreground">
                1
              </div>
              <h3 className="mb-2 font-semibold text-foreground">Share Your Code</h3>
              <p className="text-sm text-muted-foreground">
                Share your unique referral link with friends and family.
              </p>
            </div>
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-hero text-2xl font-bold text-primary-foreground">
                2
              </div>
              <h3 className="mb-2 font-semibold text-foreground">They Sign Up</h3>
              <p className="text-sm text-muted-foreground">
                They sign up to elefa and add $15 or more in calling credit within 30 days.
              </p>
            </div>
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-hero text-2xl font-bold text-primary-foreground">
                3
              </div>
              <h3 className="mb-2 font-semibold text-foreground">You Both Get $10</h3>
              <p className="text-sm text-muted-foreground">
                Once their top-up is confirmed, you both receive {bonusAmt} credit.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Referral Code Section */}
      <section className="py-16">
        <div className="container">
          <div className="mx-auto max-w-2xl">
            {user ? (
              <>
                <div className="rounded-2xl border bg-card p-8 text-center shadow-card">
                  <h2 className="mb-6 text-xl font-bold text-foreground">
                    Your Referral Link
                  </h2>
                  
                  <div className="mb-6 flex gap-2">
                    <Input
                      value={referralLink}
                      readOnly
                      className="text-center font-mono text-sm"
                    />
                    <Button onClick={copyToClipboard} variant="outline" className="gap-2">
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      {copied ? "Copied" : "Copy"}
                    </Button>
                  </div>

                  <div className="flex flex-wrap justify-center gap-3">
                    <Button variant="outline" className="gap-2" onClick={shareViaWhatsApp}>
                      <Share2 className="h-4 w-4" />
                      Share via WhatsApp
                    </Button>
                    <Button variant="outline" className="gap-2" onClick={shareViaEmail}>
                      <Share2 className="h-4 w-4" />
                      Share via Email
                    </Button>
                  </div>

                  <div className="mt-6 rounded-xl bg-muted/50 p-4">
                    <div className="text-sm text-muted-foreground">Your Referral Code</div>
                    <div className="text-2xl font-bold text-gradient">{referralCode}</div>
                  </div>
                </div>

                {/* Stats */}
                <div className="mt-8 grid gap-4 sm:grid-cols-3">
                  <div className="rounded-xl border bg-card p-4 text-center">
                    <Users className="mx-auto mb-2 h-6 w-6 text-primary" />
                    {loading ? (
                      <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
                    ) : (
                      <div className="text-2xl font-bold text-foreground">{stats.totalReferrals}</div>
                    )}
                    <div className="text-sm text-muted-foreground">Total Referrals</div>
                  </div>
                  <div className="rounded-xl border bg-card p-4 text-center">
                    <Check className="mx-auto mb-2 h-6 w-6 text-secondary" />
                    {loading ? (
                      <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
                    ) : (
                      <div className="text-2xl font-bold text-foreground">{stats.completedReferrals}</div>
                    )}
                    <div className="text-sm text-muted-foreground">Successful</div>
                  </div>
                  <div className="rounded-xl border bg-card p-4 text-center">
                    <Gift className="mx-auto mb-2 h-6 w-6 text-accent-foreground" />
                    {loading ? (
                      <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
                    ) : (
                      <div className="text-2xl font-bold text-gradient">
                        ${(stats.totalEarnedCents / 100).toFixed(0)}
                      </div>
                    )}
                    <div className="text-sm text-muted-foreground">Total Earned</div>
                  </div>
                </div>

                {/* Referral History */}
                <div className="mt-8">
                  <h3 className="mb-4 text-lg font-semibold text-foreground">
                    Referral History
                  </h3>
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                  ) : referrals.length === 0 ? (
                    <div className="rounded-xl border bg-card p-8 text-center">
                      <Users className="mx-auto mb-4 h-12 w-12 text-muted-foreground/50" />
                      <p className="text-muted-foreground">
                        No referrals yet. Share your link to start earning!
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {referrals.map((referral) => (
                        <div
                          key={referral.id}
                          className="flex items-center justify-between rounded-xl border bg-card p-4"
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted font-medium">
                              {formatDisplayName(referral)[0].toUpperCase()}
                            </div>
                            <div>
                              <div className="font-medium text-foreground">
                                {formatDisplayName(referral)}
                              </div>
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                {referral.status === 'pending' ? (
                                  <>
                                    <Clock className="h-3 w-3" />
                                    Pending activation
                                  </>
                                ) : (
                                  <>
                                    <Check className="h-3 w-3 text-secondary" />
                                    Completed
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className={referral.status === "completed" ? "text-secondary font-semibold" : "text-muted-foreground"}>
                            {referral.status === "completed" 
                              ? `+$${(referral.reward_cents / 100).toFixed(0)}` 
                              : "Pending"}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="rounded-2xl border bg-card p-8 text-center">
                <Gift className="mx-auto mb-4 h-16 w-16 text-primary" />
                <h2 className="mb-4 text-xl font-bold text-foreground">
                  Sign In to Get Your Referral Link
                </h2>
                <p className="mb-6 text-muted-foreground">
                  Create an account or sign in to start earning rewards.
                </p>
                <Link to="/auth">
                  <Button className="bg-gradient-hero hover:opacity-90 gap-2">
                    Sign In / Sign Up
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Leaderboard */}
      <section className="bg-muted/30 py-16">
        <div className="container">
          <div className="mx-auto max-w-2xl">
            <div className="mb-8 text-center">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-accent/20 px-4 py-2 text-sm font-medium text-accent-foreground">
                <Trophy className="h-4 w-4" />
                Top Referrers
              </div>
              <h2 className="text-2xl font-bold text-foreground">Leaderboard</h2>
            </div>

            {leaderboardLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="space-y-3">
                {leaderboard.map((leader, index) => (
                  <div
                    key={leader.referrer_id}
                    className="flex items-center justify-between rounded-xl border bg-card p-4"
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted font-bold text-sm">
                        {index === 0 ? "🥇" : index === 1 ? "🥈" : index === 2 ? "🥉" : index + 1}
                      </div>
                      <span className="font-medium text-foreground">{leader.display_name}</span>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {leader.completed_referrals} referrals
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Terms */}
      <section className="py-12">
        <div className="container">
          <div className="mx-auto max-w-2xl text-center text-sm text-muted-foreground">
            <h3 className="mb-4 font-semibold text-foreground">Terms & Conditions</h3>
            <p>
              Referral credit is applied when the referred person adds $15 or more in calling credit within 30 days of signing up.
              Maximum of 50 referrals per account. Credit cannot be exchanged for cash.
              Referral credit expires 12 months from the date it is credited to your account. This expiry applies to referral credit only. Purchased calling credit does not expire.
              elefa Mobile reserves the right to modify or terminate this program at any time.
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Referral;
